<template>
  <v-form ref="form" v-model="valid" validate-on="submit lazy">
    <div>
      <h3 class="py-4">{{ label }}</h3>
      <ErrorAlert
        v-if="error"
        :error="error"
        class="mb-2"
        @clearErrors="clearErrors()"
      ></ErrorAlert>
      <v-progress-circular
        v-if="isPending"
        indeterminate
        color="primary"
        size="50"
        width="50"
      ></v-progress-circular>
      <v-row v-else>
        <template v-if="type == ChitOptionType.Condition">
          <v-col
            cols="12"
            md="3"
            v-for="(option, optionIndex) in chitOptions"
            :key="optionIndex"
            class="py-0"
          >
            <v-checkbox
              :label="option.text"
              :model-value="getOptionAnswer(option)"
              @update:model-value="setOptionAnswer(option, $event)"
              hide-details="auto"
            ></v-checkbox>
          </v-col>
        </template>

        <template v-else>
          <v-col cols="12" v-for="(option, optionIndex) in chitOptions" :key="optionIndex">
            <ChitOptionsSelect
              :option="option"
              v-model="chitOptionAnswers"
              :type="type"
              @clearErrors="clearErrors()"
            />
          </v-col>
        </template>
      </v-row>
    </div>
  </v-form>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { ISystemError } from '@/models/error'
import type { VForm } from 'vuetify/components'
import { ChitOptionType, type IChitOption, type IChitOptionAnswerData } from '@/models/chit'
import { useFetchChitOptions } from '@/composables/chit'
import ErrorAlert from '../common/ErrorAlert.vue'
import ChitOptionsSelect from './ChitOptionsSelect.vue'

interface Props {
  error?: ISystemError | null
  type: ChitOptionType
  label: string
}

const props = defineProps<Props>()

const chitOptionAnswers = defineModel<IChitOptionAnswerData[]>({ default: () => [] })

const form = ref<VForm>()
const valid = ref(false)

const { isPending, chitOptions, error } = useFetchChitOptions(props.type)

async function validateForm() {
  clearErrors()
  const { valid } = await form.value!.validate()
  return valid
}

function getOptionAnswer(option: IChitOption): boolean | null {
  const answer = chitOptionAnswers.value.find((optionAnswer) => {
    const optionQuestion =
      typeof optionAnswer.option == 'number' ? optionAnswer.option : optionAnswer.option!.id
    return optionQuestion === option.id
  })
  if (!answer) {
    return null
  }
  return answer.answer ?? null
}

function setOptionAnswer(option: IChitOption, value: boolean | null) {
  const idx = chitOptionAnswers.value.findIndex((optionAnswer) => {
    const optionQuestion =
      typeof optionAnswer.option == 'number' ? optionAnswer.option : optionAnswer.option!.id
    return optionQuestion === option.id
  })
  if (idx !== -1) {
    if (props.type === ChitOptionType.Condition) {
      // If the condition is false, remove the answer
      chitOptionAnswers.value.splice(idx, 1)
    } else {
      // If the condition is true, update the answer
      chitOptionAnswers.value[idx].answer = value
    }
  } else {
    chitOptionAnswers.value.push({
      option: option,
      answer: value
    })
  }
}

function resetFormValidation() {
  form.value?.resetValidation()
}

function clearErrors() {
  resetFormValidation()
}

defineExpose({
  validateForm,
  clearErrors
})
</script>
